import React, { useState, useEffect } from "react";
import { Box, Typography, Paper, styled, useTheme } from "@mui/material";
import ChatComponent from "ui/components/chat/ChatComponent";
import WithFeedbackTabData from "ui/containers/settings/FeedbackTabContainer";
import { FeedbackTabPropType, FeedbackInterface } from "entities/interfaces";
import useScreenSize from "hooks/ScreenSize";
import { MobileWidth } from "entities/constants";
import { useTranslation } from "react-i18next";

// === Styled Components ===
const OuterWrapper = styled(Box)(({ theme }) => ({
  width: "100%",
  padding: "2rem",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  [theme.breakpoints.down("sm")]: {
    padding: "1rem",
  },
}));

const Card = styled(Paper)(({ theme }) => ({
  width: "100%",
  maxWidth: "960px",
  borderRadius: "16px",
  padding: "2rem",
  display: "flex",
  flexDirection: "column",
  gap: "1.5rem",
  boxShadow: theme.shadows[3],
  backgroundColor: theme.palette.background.paper,
  [theme.breakpoints.down("sm")]: {
    padding: "1rem",
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: "1.5rem",
  fontWeight: 700,
  color: theme.palette.text.primary,
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize,
  color: theme.palette.text.secondary,
}));

const ChatWrapper = styled(Box)(() => ({
  flex: 1,
  minHeight: "400px",
  maxHeight: "600px",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
}));

// === Component ===
const FeedbackTab = ({ feedbackData, addFeedback }: FeedbackTabPropType) => {
  const [sendDisabled, setSendDisabled] = useState(false);
  const { width } = useScreenSize();
  const { t, i18n } = useTranslation();

  const [clickedIndexes, setClickedIndexes] = useState<Set<number>>(() => {
    const saved = localStorage.getItem("clickedIndexes");
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });

  const [npsSubmitted, setNpsSubmitted] = useState(false);
  const [localMessages, setLocalMessages] = useState<FeedbackInterface[]>(feedbackData);

  useEffect(() => {
    setLocalMessages(feedbackData);
  }, [feedbackData]);

  const handleSendFeedback = async (message: string) => {
    setSendDisabled(true);

    const userMessage: FeedbackInterface = {
      id: Date.now(),
      feedback: message,
      time: new Date().toISOString(),
      direction: "user",
    };

    const confirmation: FeedbackInterface = {
      id: Date.now() + 1,
      feedback: i18n.language === "de" ? "Danke für dein Feedback!" : "Thank you for your feedback!",
      time: new Date().toISOString(),
      direction: "system",
    };

    // Zeige direkt im Chat
    setLocalMessages((prev) => [...prev, userMessage, confirmation]);

    // Sende ans Backend
    await addFeedback(message);

    setSendDisabled(false);
  };

  return (
    <OuterWrapper sx={width <= MobileWidth ? { padding: "1rem 0.5rem" } : {}}>
      <Card>
        <Box>
          <Title>{t("Feedback")}</Title>
          <Subtitle>{t("Let us know what you think about your experience.")}</Subtitle>
        </Box>

        <ChatWrapper>
          <ChatComponent
            messagesData={localMessages}
            sendMessage={handleSendFeedback}
            sendDisabled={sendDisabled}
            typingAllowed={true}
            onUserInput={() => {}}
            onSubmit={() => {}}
            clickedIndexes={clickedIndexes}
            setClickedIndexes={setClickedIndexes}
            handleSaveProgress={() => {}}
            npsSubmitted={npsSubmitted}
            setNpsSubmitted={setNpsSubmitted}
          />
        </ChatWrapper>
      </Card>
    </OuterWrapper>
  );
};

export default WithFeedbackTabData(FeedbackTab);
